/**
 * useUnreadCountSocket - Global socket listener for unread count updates.
 * Updates React Query caches for all unread counters (chat + email).
 * Must be mounted at a global level (e.g., Header) to work on all pages.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS as Events } from '@troia-v3/types';
import type { UnreadCountUpdateEvent } from '@troia-v3/types';
import { useAuth } from '@/contexts/AuthContext';

export const useUnreadCountSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { state: authState } = useAuth();

  useEffect(() => {
    if (!socket || !authState.user) return;

    const handleUnreadCountUpdate = (data: UnreadCountUpdateEvent) => {
      // Chat counters
      queryClient.setQueryData(['unread-count'], data.totalUnread);
      queryClient.setQueryData(['conversations', 'unread-count', 'ai'], data.byType.ai);
      queryClient.setQueryData(['conversations', 'unread-count', 'individual'], data.byType.individual);
      queryClient.setQueryData(['conversations', 'unread-count', 'group'], data.byType.group);

      // Email counter
      if (data.byType.email !== undefined) {
        queryClient.setQueryData(['email-unread-count'], data.byType.email);
      }
    };

    socket.on(Events.UNREAD_COUNT_UPDATE, handleUnreadCountUpdate);

    return () => {
      socket.off(Events.UNREAD_COUNT_UPDATE, handleUnreadCountUpdate);
    };
  }, [socket, authState.user, queryClient]);
};
